import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import {
  DialogHeader,
  DialogFooter,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { useModal } from "@/lib/modals/useModal";
import { AlertOctagon } from "lucide-react";
import { useMutateEndCustomerTag } from "@/lib/hooks/query/endcustomer/useMutateEndCustomerTag";
import { EndCustomer } from "@/api/generated/data-contracts";
import { Input } from "@/components/ui/input";
import * as z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Label } from "@radix-ui/react-label";
import { ENV } from "@/env";
import { useQueryClient } from "@tanstack/react-query";

const FormSchema = z.object({
  tag: z
    .string()
    .min(2, "The tag must be at least 2 characters long")
    .max(3, "The tag must be at most 3 characters long")
    .regex(
      /^[a-zA-Z0-9-]{2,3}$/,
      "The tag must contain only letters, numbers and hyphens",
    ),
});

type FormValues = z.infer<typeof FormSchema>;

interface MissingTagModalProps {
  endCustomer: EndCustomer;
  onCancel?: () => void;
}

export function MissingTagModal({
  endCustomer,
  onCancel,
}: Readonly<MissingTagModalProps>) {
  const queryClient = useQueryClient();
  const { t } = useTranslation(undefined, {
    keyPrefix: "inbound.modal.missing-tag",
  });
  const { hideModal } = useModal();

  const mutateTag = useMutateEndCustomerTag();

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    mode: "onChange",
    defaultValues: {
      tag: "",
    },
  });

  const handleCancel = () => {
    onCancel?.();
    hideModal();
  };

  const handleSubmit = async (values: FormValues) => {
    await mutateTag.mutateAsync({
      endCustomerId: endCustomer.id,
      tag: values.tag,
    });
    await queryClient.invalidateQueries({
      queryKey: ["endCustomer", endCustomer.id],
    });
    hideModal();
  };

  const linkUrl =
    ENV.portalUrl + "/partners/" + endCustomer.partner?.id + "/customers";

  return (
    <DialogContent className="sm:max-w-md">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="flex flex-col gap-6"
        >
          <DialogHeader className="flex flex-col items-center">
            <AlertOctagon className="size-12" />
            <DialogTitle>{t("stop")}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-3 text-center">
            <span>{t("description-text")}</span>
            <span>
              {t("portal-description-text-start")}{" "}
              <a href={linkUrl}>[{t("link")}]</a>{" "}
              {t("portal-description-text-end")}
            </span>
          </div>
          <div className="flex flex-col gap-4">
            <Label className="flex gap-2">
              <span>{t("end-customer-name")}:</span>
              <span className="font-bold text-gray-500">
                {endCustomer.name}
              </span>
            </Label>
            <FormField
              control={form.control}
              name="tag"
              render={({ field }) => (
                <FormItem className="h-20 w-full">
                  <FormLabel>{t("end-customer-tag")}</FormLabel>
                  <FormControl>
                    <Input {...field} data-testid="tag-input" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <DialogFooter className="mt-6 grid grid-cols-2 gap-5">
            <Button variant="secondary" onClick={handleCancel}>
              {t("cancel")}
            </Button>
            <Button type="submit" disabled={!form.formState.isValid}>
              {t("save")}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </DialogContent>
  );
}
